import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, Settings } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { LANG } from "@/lib/i18n/lang";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { useWebSocket } from "@/hooks/useWebSocket";

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState("all"); // all | unread
  const [loading, setLoading] = useState(true);
  useDocumentTitle("Notifications");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 100, unread_only: filter === "unread" } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (_) { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  // Reload when a WS event lands
  useWebSocket({ onMessage: (msg) => {
    if (msg?.type === "message" || msg?.type === "notification") load();
  }});

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); load(); } catch (_) {}
  };

  const markOne = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); } catch (_) {}
  };

  return (
    <DashboardShell>
      <div className="max-w-4xl">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Notifications</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Everything happening on your account.</h1>
            <p className="text-[#252A34]/70 text-sm mt-1">{unread} unread</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/dashboard/notifications/preferences" className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium" data-testid="notif-open-prefs">
              <Settings className="h-4 w-4" /> Preferences
            </Link>
            <button onClick={markAll} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="notif-mark-all">
              <Check className="h-4 w-4" /> Mark all read
            </button>
          </div>
        </div>

        <div className="mb-4 inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="notif-filter">
          {[
            { k: "all", label: "All" },
            { k: "unread", label: "Unread" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setFilter(t.k)}
              data-testid={`notif-filter-${t.k}`}
              className={`px-3 py-1.5 text-xs rounded-full ${filter === t.k ? "bg-[#165a71] text-white" : "text-[#252A34]/80"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-[#252A34]/60">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <Bell className="h-6 w-6 mx-auto text-[#165a71]" />
            <div className="mt-2 font-display text-lg text-[#071b22] font-semibold">You're all caught up.</div>
            <div className="text-sm text-[#252A34]/70 mt-1">We'll ping you when something needs attention.</div>
          </div>
        ) : (
          <ul className="rounded-2xl border border-slate-200 bg-white divide-y" data-testid="notif-list">
            {items.map((n) => (
              <li key={n.id} className={`p-4 flex items-start gap-3 ${!n.read_at ? "bg-[#165a71]/5" : ""}`} data-testid={`notif-row-${n.id}`}>
                <div className="mt-1 h-2 w-2 rounded-full shrink-0 bg-[#165a71]" style={{ opacity: n.read_at ? 0.2 : 1 }} />
                <div className="flex-1 min-w-0">
                  <div translate={n.metadata?.lang === "es" ? "no" : undefined} className="font-medium text-[#071b22] text-sm">{n.title}</div>
                  <div translate={n.metadata?.lang === "es" ? "no" : undefined} className="text-sm text-[#252A34]/80 mt-0.5 whitespace-pre-line">{n.body}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#252A34]/50 mt-1">
                    {n.event.replace(/\./g, " · ").replace(/_/g, " ")} · {n.created_at ? new Date(n.created_at).toLocaleString(LANG === "es" ? "es-US" : "en-US") : ""}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {n.action_url && (
                    <Link to={n.action_url} onClick={() => markOne(n.id)} className="text-xs font-medium text-[#165a71]" data-testid={`notif-open-${n.id}`}>Open</Link>
                  )}
                  {!n.read_at && (
                    <button onClick={() => markOne(n.id)} className="text-[10px] text-[#252A34]/60 hover:text-[#165a71]" data-testid={`notif-mark-${n.id}`}>Mark read</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </DashboardShell>
  );
}
